import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useBusinessPartnersStore } from '../../lib/businessPartners';
import { usePackagesStore } from '../../lib/packages';
import BusinessOfferBookingForm from '../../components/admin/BusinessOfferBookingForm';
import EditOfferModal from '../../components/admin/partner/EditOfferModal';
import OfferDetailsModal from '../../components/admin/partner/OfferDetailsModal';
import AcceptOfferForm from '../../components/admin/partner/AcceptOfferForm';
import type { 
  BusinessOffer, 
  BusinessOfferBooking,
  BookingFormData 
} from '../../types/business';
import { X } from 'lucide-react';
import { 
  GET_BUSINESS, 
  GET_BUSINESS_OFFERS 
} from '../../graphql/admin/queries/business';
import { ACCEPT_BUSINESS_OFFER } from '../../graphql/admin/mutations/business';
import { getImageUrl } from '../../utils/imageUrl';

const REJECT_OFFER = gql`
  mutation RejectOffer($offerId: Int!) {
    rejectBusinessOffer(offerId: $offerId) {
      id
      status
    }
  }
`;

const UPDATE_OFFER_BOOKINGS = gql`
  mutation UpdateOfferBookings($offerId: Int!, $input: BusinessOfferUpdateInput!) {
    updateBusinessOffer(offerId: $offerId, input: $input) {
      id
      bookings {
        id
        travelerInfo {
          firstName
          lastName
          email
          phone
          passport {
            number
            expiryDate
            file
          }
        }
        roomPreferences {
          type
          quantity
        }
        status
      }
    }
  }
`;

export default function BusinessPartnerPortal() {
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<number | null>(null);
  const [selectedOfferForDetails, setSelectedOfferForDetails] = useState<number | null>(null);
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [selectedOfferForAccept, setSelectedOfferForAccept] = useState<BusinessOffer | null>(null);

  const { data: businessData, loading: businessLoading } = useQuery(GET_BUSINESS, {
    variables: { id: parseInt(partnerId || '0') }
  });

  const { 
    data: offersData,
    loading: offersLoading,
    refetch
  } = useQuery(GET_BUSINESS_OFFERS, {
    variables: { businessId: parseInt(partnerId || '0') }
  });

  const [acceptBusinessOffer] = useMutation(ACCEPT_BUSINESS_OFFER);

  const [rejectOffer] = useMutation(REJECT_OFFER, {
    onCompleted: () => refetch()
  });

  const [updateOfferBookings] = useMutation(UPDATE_OFFER_BOOKINGS, {
    onCompleted: () => {
      refetch();
      setShowBookingForm(false);
      setSelectedOffer(null);
    }
  });

  if (businessLoading || offersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const business = businessData?.business;
  const offers = offersData?.businessOffers || [];



  const handleAcceptOffer = (offerId: number) => {
    const selectedOffer = offers.find((o: BusinessOffer) => o.id === offerId);
    if (!selectedOffer) return;
    
    setSelectedOfferForAccept(selectedOffer);
    setShowAcceptForm(true);
  };

  const handleRejectOffer = async (offerId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir refuser cette offre ?')) {
      try {
        await rejectOffer({
          variables: { offerId }
        });
      } catch (error) {
        console.error('Erreur:', error);
      }
    }
  };

  const handleEditOffer = (offerId: string) => {
    setSelectedOffer(parseInt(offerId));
    setShowEditModal(true);
  };

  const handleUpdateBookings = async (offerId: number, bookings: any) => {
    try {
      await updateOfferBookings({
        variables: {
          offerId,
          input: bookings.map((booking: any) => ({
            travelerInfo: booking.travelers.map((traveler: any) => ({
              firstName: traveler.firstName,
              lastName: traveler.lastName,
              email: traveler.email,
              phone: traveler.phone,
              passport: traveler.passport
            })),
            roomPreferences: booking.roomPreferences.map(room => ({
              type: room.type,
              quantity: room.count
            })),
            status: 'CONFIRMED'
          }))
        }
      });
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleBookingSubmit = async (bookingData: BookingFormData) => {
    try {
      await updateOfferBookings({
        variables: {
          offerId: selectedOffer,
          input: bookingData.bookings.map((booking: BusinessOfferBooking) => ({
            travelerInfo: booking.travelers.map(traveler => ({
              firstName: traveler.firstName,
              lastName: traveler.lastName,
              email: traveler.email,
              phone: traveler.phone,
              passport: traveler.passport
            })),
            roomPreferences: booking.roomPreferences.map(room => ({
              type: room.type,
              quantity: room.count
            })),
          }))
        }
      });
      refetch();
      setShowBookingForm(false);
      setSelectedOffer(null);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Trouver l'offre sélectionnée pour les détails
  const selectedOfferDetails = selectedOfferForDetails 
    ? offersData?.businessOffers.find((offer: any) => offer.id === selectedOfferForDetails)
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* En-tête */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center">
            {business?.logo && (
              <img
                src={getImageUrl(business.logo)}
                alt={business.companyName}
                className="h-12 w-12 rounded-full mr-4"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold">{business?.companyName}</h1>
              <p className="text-gray-600">Espace partenaire</p>
            </div>
          </div>
        </div>
      </header>

      {/* Liste des offres */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <h2 className="text-xl font-bold">Offres en cours</h2>
          {offersLoading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : offers.length === 0 ? (
            <p className="text-gray-600 text-center py-8">Aucune offre disponible</p>
          ) : (
            <div className="grid gap-6">
              {offers.map(offer => (
                <div key={offer.id} className="bg-white shadow rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">{offer.title}</h3>
                      <p className="text-gray-600">{offer.description}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      offer.status === 'ACCEPTED' ? 'bg-green-100 text-green-800' :
                      offer.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {offer.status === 'ACCEPTED' ? 'Acceptée' :
                       offer.status === 'REJECTED' ? 'Refusée' :
                       'En attente'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">
                        Valable jusqu'au {new Date(parseInt(offer.validUntil)).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      <p className="text-sm text-gray-500">
                        Réduction : {offer.discount}%
                      </p>
                      <p className="text-sm text-gray-500">
                        Package : {offer.package.title}
                      </p>
                    </div>

                    <div className="flex gap-4">
                      <button
                        onClick={() => setSelectedOfferForDetails(offer.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Voir les détails
                      </button>

                      {offer.status === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleAcceptOffer(offer.id)}
                            className="px-4 py-2 text-green-600 border border-green-600 rounded hover:bg-green-50"
                          >
                            Accepter
                          </button>
                          <button
                            onClick={() => handleRejectOffer(offer.id)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            Refuser
                          </button>
                        </>
                      )}

                      {offer.status === 'ACCEPTED' && (
                        <button
                          onClick={() => handleEditOffer(offer.id.toString())}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Modifier les voyageurs
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Afficher les réservations si elles existent */}
                  {(offer.bookings || []).length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-medium mb-2">Réservations</h4>
                      {(offer.bookings || []).map(booking => (
                        <div key={booking.id} className="bg-gray-50 p-4 rounded-lg">
                          <p className="font-medium">
                            {Array.isArray(booking.travelerInfo) ? booking.travelerInfo.length : 0} voyageur(s)
                          </p>
                          <div className="mt-2">
                            <h5 className="text-sm font-medium text-gray-700">Chambres :</h5>
                            {Array.isArray(booking.roomPreferences) && booking.roomPreferences.map((room, index) => (
                              <p key={index} className="text-sm text-gray-600">
                                {room.quantity}x {room.type.toLowerCase()}
                              </p>
                            ))}
                          </div>
                          <p className="mt-2 text-sm text-gray-600">
                            Statut : <span className={`font-medium ${
                              booking.status === 'CONFIRMED' ? 'text-green-600' :
                              booking.status === 'CANCELLED' ? 'text-red-600' :
                              'text-yellow-600'
                            }`}>
                              {booking.status === 'CONFIRMED' ? 'Confirmée' :
                               booking.status === 'CANCELLED' ? 'Annulée' :
                               'En attente'}
                            </span>
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            Créée le {new Date(booking.createdAt).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {offer.package && (
                    <div>
                      <h3>{offer.package.title}</h3>
                      {offer.package.formulas && offer.package.formulas.length > 0 && (
                        <div>
                          <h4>Formules:</h4>
                          <ul>
                            {offer.package.formulas.map(formula => (
                              <li key={formula.id}>{formula.name}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {selectedOffer && (
        <>
          <BusinessOfferBookingForm
            isOpen={showBookingForm}
            onClose={() => {
              setShowBookingForm(false);
              setSelectedOffer(null);
            }}
            offerId={selectedOffer}
            minimumParticipants={offers.find(o => o.id === selectedOffer)?.minimumParticipants || 1}
            onSubmit={handleBookingSubmit}
          />

          <EditOfferModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedOffer(null);
            }}
            offer={offers.find(o => o.id === selectedOffer)!}
          />
        </>
      )}

      {selectedOfferForDetails && selectedOfferDetails && (
        <OfferDetailsModal
          isOpen={!!selectedOfferForDetails}
          onClose={() => setSelectedOfferForDetails(null)}
          offerId={selectedOfferForDetails}
          offer={selectedOfferDetails}
        />
      )}

      {/* Modal pour accepter l'offre */}
      {showAcceptForm && selectedOfferForAccept && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Accepter l'offre</h2>
                <button
                  onClick={() => {
                    setShowAcceptForm(false);
                    setSelectedOfferForAccept(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <AcceptOfferForm
                offer={selectedOfferForAccept}
                onSuccess={() => {
                  setShowAcceptForm(false);
                  setSelectedOfferForAccept(null);
                  refetch();
                }}
                onClose={() => {
                  setShowAcceptForm(false);
                  setSelectedOfferForAccept(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}